import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import useSEO from "@/lib/useSEO";
import ForkliftCard from "@/components/ForkliftCard";
import QuickPhotoUpload from "@/components/QuickPhotoUpload";
import Testimonials from "@/components/Testimonials";
import { BRANDS, SUBURBS } from "@/lib/data";
import { Wrench, CalendarCheck, Truck, ShoppingBag, MapPin, ShieldCheck, Cog, Zap, Gauge, ArrowRight, CheckCircle2 } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1735494034625-86409b829b33?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxmb3JrbGlmdCUyMHRlY2huaWNpYW4lMjByZXBhaXJpbmd8ZW58MHx8fHwxNzg4MDgxMTUyfDA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  { icon: Wrench, title: "Forklift Service & Repairs", desc: "On-site diagnostics, breakdown repairs and mechanical, hydraulic and electrical repairs for a wide range of forklift brands.", cta: "Book a service", to: "/book-service" },
  { icon: CalendarCheck, title: "Planned Maintenance", desc: "Reduce breakdowns and downtime with scheduled forklift servicing and preventative maintenance tailored to your fleet.", cta: "View maintenance", to: "/forklift-maintenance-brisbane" },
  { icon: Truck, title: "Forklift Hire", desc: "Short-, medium- and long-term forklift hire for warehouses, construction, logistics and industrial operations.", cta: "View hire fleet", to: "/forklift-hire-brisbane" },
  { icon: ShoppingBag, title: "Used Forklifts", desc: "Quality used LPG, diesel and electric forklifts available for Brisbane businesses — warehousing, logistics, construction and industrial.", cta: "View forklifts", to: "/forklifts-for-sale" },
];

const BENEFITS = [
  { icon: Truck, t: "Mobile Workshop", d: "We come directly to your site, reducing unnecessary forklift downtime." },
  { icon: Cog, t: "Multi-Brand Experience", d: "Service and repairs across many leading forklift manufacturers." },
  { icon: Gauge, t: "Diagnostics & Repairs", d: "Mechanical, hydraulic, LPG and electrical fault diagnosis." },
  { icon: CalendarCheck, t: "Preventative Maintenance", d: "Scheduled servicing designed to improve reliability and equipment life." },
  { icon: Wrench, t: "Parts & Repairs", d: "Parts sourcing and replacement for common forklift systems and components." },
  { icon: ShieldCheck, t: "Flexible Support", d: "Support for businesses with a single forklift through to larger fleets." },
];

export default function Home() {
  useSEO({
    title: "Forkfix — Forklift Service, Repairs, Hire & Sales in Brisbane",
    description: "Brisbane's specialist forklift service, repairs, maintenance, hire and used sales. Mobile technicians, multi-brand experience. Call 0482 712 785. ABN 61 624 878 276.",
    canonical: "https://forkfix.net/",
  });
  const [featured, setFeatured] = useState([]);
  useEffect(() => { api.get("/forklifts", { params: { featured: true, limit: 6 } }).then(r => setFeatured(r.data)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-[#111111] text-white overflow-hidden">
        <div className="absolute inset-0"><img src={HERO_IMG} alt="Forklift technician" className="w-full h-full object-cover opacity-40" /></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#111111]/95 via-[#111111]/80 to-[#111111]/40 ff-diagonal-bg"></div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-white/20 px-4 py-1.5 text-xs md:text-sm uppercase tracking-widest text-white/85 mb-6">
              <span className="w-2 h-2 bg-[#D71920]"></span> Brisbane Local · Same-Day Mobile Service
            </div>
            <h1 className="font-display font-extrabold text-5xl md:text-7xl leading-[0.95] uppercase">
              Forklift Service,<br/>Repairs, Hire &<br/><span className="text-[#D71920]">Sales in Brisbane</span>
            </h1>
            <p className="mt-6 text-lg text-white/80 max-w-2xl">Brisbane-based forklift specialist. On-site mobile repairs, preventative maintenance, diagnostics and servicing — plus flexible forklift hire and quality used forklifts for sale. Local technicians, local parts, local turnaround.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/book-service" className="ff-btn-primary" data-testid="hero-book-btn"><Wrench className="w-4 h-4" /> Book a Service</Link>
              <Link to="/contact" className="ff-btn-ghost !border-white !text-white hover:!bg-white hover:!text-black" data-testid="hero-quote-btn">Request a Quote</Link>
              <Link to="/forklifts-for-sale" className="inline-flex items-center gap-2 px-2 py-3 text-sm uppercase tracking-widest font-bold text-white/80 hover:text-white">View Forklifts <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              {["Mobile Service","Multi-Brand Repairs","Brisbane & Surrounds","Commercial & Industrial"].map(t => (
                <div key={t} className="flex items-center gap-2 text-white/85"><CheckCircle2 className="w-4 h-4 text-[#D71920] shrink-0" /> {t}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROMO STRIP */}
      <section className="bg-[#D71920] text-white animate-popin" data-testid="promo-strip">
        <div className="max-w-7xl mx-auto px-6 py-6 md:py-7 flex flex-col md:flex-row items-center gap-5 md:gap-8">
          <div className="hidden md:flex items-center justify-center w-14 h-14 border-2 border-white/60 shrink-0 animate-pulse-ring">
            <Wrench className="w-7 h-7" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-white/80">Limited-time promotion</div>
            <div className="font-display text-xl md:text-2xl uppercase mt-1 leading-tight">
              Basic engine service, safety checks &amp; machine lubrication
            </div>
          </div>
          <div className="flex items-center gap-4 md:gap-6">
            <div className="text-right">
              <div className="font-display text-3xl md:text-4xl leading-none">$390</div>
              <div className="text-[10px] uppercase tracking-widest text-white/80 mt-1">GST inc.</div>
            </div>
            <Link to="/book-service" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-[#D71920] transition whitespace-nowrap" data-testid="promo-book-btn">
              Book Now <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FOUR SERVICES */}
      <section className="max-w-7xl mx-auto px-6 py-16 md:py-24">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold">What we do</div>
          <h2 className="ff-accent-heading font-display text-4xl md:text-5xl uppercase mt-2">One contact for your forklift needs</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map(({ icon: I, title, desc, cta, to }) => (
            <div key={title} className="ff-card p-6 flex flex-col">
              <I className="w-8 h-8 text-[#D71920]" strokeWidth={2} />
              <h3 className="font-display text-2xl mt-4 uppercase leading-tight">{title}</h3>
              <p className="text-sm text-[#555] mt-3 flex-1">{desc}</p>
              <Link to={to} className="mt-5 text-xs uppercase tracking-widest font-bold flex items-center gap-1 hover:text-[#D71920]" data-testid={`svc-card-${title}`}>{cta} <ArrowRight className="w-3.5 h-3.5" /></Link>
            </div>
          ))}
        </div>
      </section>

      {/* BOOKING SECTION */}
      <section className="bg-[#111111] text-white ff-dotgrid-bg">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold">Online service request</div>
            <h2 className="font-display text-4xl md:text-6xl uppercase mt-3 leading-[0.95]">Need a forklift<br/>technician?</h2>
            <p className="mt-5 text-white/75 max-w-lg">Book a service visit online and provide us with the details of your forklift and the problem you're experiencing.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/book-service" className="ff-btn-primary" data-testid="booking-cta-primary"><Wrench className="w-4 h-4" /> Book Service Online</Link>
              <Link to="/book-service?urgency=breakdown" className="ff-btn-ghost !border-white !text-white hover:!bg-white hover:!text-black" data-testid="booking-cta-breakdown">Request Breakdown</Link>
            </div>
            <p className="mt-6 text-xs text-white/50 italic">Requested appointment times are subject to confirmation by Forkfix.</p>
          </div>
          <div className="space-y-4">
            {[
              ["01","Tell us about your forklift","Enter make, model, fuel type and problem."],
              ["02","Select your preferred date","Choose when you'd like our technician to attend."],
              ["03","Forkfix confirms the booking","Our team reviews your request and confirms the appointment."],
            ].map(([n,t,d]) => (
              <div key={n} className="flex gap-5 p-5 border border-white/15 bg-white/5">
                <div className="font-display text-5xl font-extrabold text-[#D71920] leading-none">{n}</div>
                <div><div className="font-display text-xl uppercase">{t}</div><div className="text-sm text-white/70 mt-1">{d}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-7xl mx-auto px-6 py-16 md:py-24">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold">Why Forkfix</div>
          <h2 className="ff-accent-heading font-display text-4xl md:text-5xl uppercase mt-2">Your local forklift specialist</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#e5e5e5] border border-[#e5e5e5]">
          {BENEFITS.map(({ icon: I, t, d }) => (
            <div key={t} className="bg-white p-8">
              <I className="w-7 h-7 text-[#D71920]" />
              <div className="font-display text-2xl uppercase mt-4">{t}</div>
              <div className="text-sm text-[#555] mt-2">{d}</div>
            </div>
          ))}
        </div>
      </section>

      <QuickPhotoUpload />

      {/* MOBILE WORKSHOP */}
      <section className="bg-[#f4f4f4]">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src="/forkfix-van.jpg" alt="Forkfix mobile workshop van with red Forkfix branding on the side" className="w-full h-auto border border-[#e5e5e5] shadow-[8px_8px_0_#D71920]" loading="lazy" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold">Mobile workshop</div>
            <h2 className="ff-accent-heading font-display text-4xl md:text-5xl uppercase mt-2">We come to your site</h2>
            <p className="mt-5 text-[#333] text-lg leading-relaxed">Our mobile workshop enables Forkfix to carry out servicing, diagnostics and many repairs directly at your workplace. This helps reduce unnecessary equipment transport and gets your forklift back into operation sooner.</p>
            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              {["Fully equipped mobile van","Diagnostic tools onboard","Common parts carried","Brisbane-wide coverage"].map(t => (
                <div key={t} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#D71920] shrink-0" /> {t}</div>
              ))}
            </div>
            <Link to="/book-service" className="ff-btn-primary mt-8" data-testid="van-book-cta"><Wrench className="w-4 h-4" /> Book On-site Service</Link>
          </div>
        </div>
      </section>

      {/* BRANDS */}
      <section className="bg-[#f4f4f4] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold text-center">Multi-brand experience</div>
          <h2 className="font-display text-4xl md:text-5xl uppercase text-center mt-2">Forklift brands we service</h2>
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-px bg-[#e5e5e5] border border-[#e5e5e5] mt-10">
            {BRANDS.map(b => (
              <div key={b} className="bg-white py-8 text-center">
                <span className="font-display font-black text-lg uppercase tracking-widest text-[#666]">{b}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-[#555] mt-6">Don't see your forklift brand? <Link to="/contact" className="text-[#D71920] underline font-semibold">Contact Forkfix</Link> — we service many other makes and models.</p>
        </div>
      </section>

      {/* FEATURED FORKLIFTS */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-16 md:py-24">
          <div className="flex flex-wrap justify-between items-end mb-8 gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold">Available now</div>
              <h2 className="ff-accent-heading font-display text-4xl md:text-5xl uppercase mt-2">Featured forklifts</h2>
            </div>
            <div className="flex gap-3">
              <Link to="/forklifts-for-sale" className="ff-btn-ghost text-xs" data-testid="home-view-sale">View for Sale</Link>
              <Link to="/forklift-hire-brisbane" className="ff-btn-dark text-xs" data-testid="home-view-hire">View for Hire</Link>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map(f => <ForkliftCard key={f.forklift_id} f={f} />)}
          </div>
        </section>
      )}

      {/* WORKSHOP */}
      {/* SERVICE AREA */}
      <section className="bg-[#111111] text-white ff-diagonal-bg">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold">Coverage</div>
            <h2 className="font-display text-4xl md:text-5xl uppercase mt-2">Mobile forklift service across Brisbane</h2>
            <p className="mt-5 text-white/75 max-w-lg">Forkfix is a locally-owned Brisbane business. We cover every major industrial pocket in the city — from the Port and Eagle Farm through the inner south to Logan and Ipswich. Typical response: same day.</p>
            <Link to="/contact" className="ff-btn-primary mt-8" data-testid="area-cta"><MapPin className="w-4 h-4" /> Check if we service your area</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {SUBURBS.map(s => (
              <div key={s} className="border border-white/15 px-3 py-3 text-sm bg-white/5 flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-[#D71920]" /> {s}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* FINAL CTA */}
      <section className="bg-[#D71920] text-white">
        <div className="max-w-7xl mx-auto px-6 py-14 md:py-20 flex flex-wrap items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-4xl md:text-5xl uppercase">Need your forklift serviced?</h2>
            <p className="mt-2 text-white/90">Tell us what you need and we'll help organise the right solution.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/book-service" className="inline-flex items-center gap-2 bg-black text-white px-6 py-4 text-sm uppercase tracking-widest font-bold hover:bg-white hover:text-black transition" data-testid="final-book-btn"><Wrench className="w-4 h-4" /> Book a Service</Link>
            <Link to="/forklift-safety-inspection" className="inline-flex items-center gap-2 bg-white text-[#D71920] px-6 py-4 text-sm uppercase tracking-widest font-bold hover:bg-black hover:text-white transition" data-testid="final-inspection-btn"><ShieldCheck className="w-4 h-4" /> Book a Safety Inspection</Link>
            <a href="tel:+61482712785" className="inline-flex items-center gap-2 border border-white text-white px-6 py-4 text-sm uppercase tracking-widest font-bold hover:bg-white hover:text-[#D71920]" data-testid="final-call-btn"><Zap className="w-4 h-4" /> Call for Assistance</a>
          </div>
        </div>
      </section>
    </div>
  );
}
